import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import orderBy from 'lodash.orderby';
import { useTranslation } from './useTranslation';

export const useLocations = () => {
    const locations = useSelector(state => state.account?.account?.companies || []);
    const { t } = useTranslation();

    const locationsMap = useMemo(
        () =>
            locations.reduce((acc, location) => {
                acc[location.companyId] = location;
                return acc;
            }, {}),
        [locations],
    );

    const getLocationsOptions = filterCb => {
        const hasFilterCb = typeof filterCb === 'function';
        const filteredLocations = hasFilterCb ? locations.filter(filterCb) : locations;
        return orderBy(
            filteredLocations.map(item => ({
                id: item.companyId,
                label: item.internalName,
                group: item.brand ? item.brand.title : `(${t('common.none')})`,
            })),
            ['group'],
            ['desc'],
        );
    };

    const getSelectLocationsOptions = filterCb => {
        const hasFilterCb = typeof filterCb === 'function';
        const filteredLocations = hasFilterCb ? locations.filter(filterCb) : locations;
        const groupedLocations = filteredLocations.reduce((acc, item) => {
            const groupName = item.brand ? item.brand.title : `(${t('common.none')})`;
            if (!acc[groupName]) {
                acc[groupName] = [];
            }
            acc[groupName].push({
                value: item.companyId,
                label: item.internalName,
            });
            return acc;
        }, {});

        return orderBy(
            Object.entries(groupedLocations).map(([group, children]) => ({
                group,
                children,
            })),
            ['group'],
            ['desc'],
        );
    };

    const getLocationsOptionsByBrand = brandId => {
        if (!brandId) return [];
        const filteredLocations = locations.filter(item => item.brand?.id === brandId);
        return orderBy(
            filteredLocations.map(item => ({
                id: item.companyId,
                label: item.internalName,
                group: item.brand ? item.brand.title : `(${t('common.none')})`,
            })),
            ['group'],
            ['desc'],
        );
    };

    const mapSelectedLocationsToFormValue = selectedLocations => {
        if (!selectedLocations.length) return [];
        return selectedLocations
            .map(selectedLocation => {
                const selectedCompany = locationsMap[selectedLocation];
                if (!selectedCompany) return undefined;
                return {
                    id: selectedCompany.companyId,
                    label: selectedCompany.internalName,
                    group: selectedCompany.brand ? selectedCompany.brand.title : `(${t('common.none')})`,
                };
            })
            .filter(Boolean);
    };

    const mapLocationsToFormValue = filteredLocations =>
        (filteredLocations || locations).map(location => ({
            id: location.companyId,
            label: location.internalName,
            group: location.brand ? location.brand.title : `(${t('common.none')})`,
        }));

    return {
        mapLocationsToFormValue,
        mapSelectedLocationsToFormValue,
        getLocationsOptionsByBrand,
        getSelectLocationsOptions,
        getLocationsOptions,
        locationsMap,
    };
};
